import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Alert,
  App,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  FileTextOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ApiOutlined,
  CloudDownloadOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/adminAPI';

const { Title, Text } = Typography;

interface ImportLog {
  id: string;
  timestamp: string;
  type: 'scheduled' | 'manual';
  provider: 'gamepix' | 'gamemonetize' | 'all';
  status: 'success' | 'error' | 'running';
  message: string;
  details?: {
    imported?: number;
    failed?: number;
    skipped?: number;
    error?: string;
  };
}

const ImportLogs: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    error: 0,
    running: 0,
  });

  useEffect(() => {
    loadLogs();
    // Auto-refresh every 10 seconds if there are running imports
    const interval = setInterval(() => {
      if (stats.running > 0) {
        loadLogs();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [stats.running]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getImportLogs();
      if (response.success) {
        setLogs(response.data);
        calculateStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
      messageApi.error('Failed to load import logs');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (logData: ImportLog[]) => {
    const stats = {
      total: logData.length,
      success: logData.filter((log) => log.status === 'success').length,
      error: logData.filter((log) => log.status === 'error').length,
      running: logData.filter((log) => log.status === 'running').length,
    };
    setStats(stats);
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'success':
        return <Tag color="success" icon={<CheckCircleOutlined />}>Success</Tag>;
      case 'error':
        return <Tag color="error" icon={<CloseCircleOutlined />}>Error</Tag>;
      case 'running':
        return <Tag color="processing" icon={<SyncOutlined spin />}>Running</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getProviderTag = (provider: string) => {
    switch (provider) {
      case 'gamepix':
        return (
          <Tag color="blue" icon={<ApiOutlined />}>
            GamePix
          </Tag>
        );
      case 'gamemonetize':
        return (
          <Tag color="cyan" icon={<CloudDownloadOutlined />}>
            GameMonetize
          </Tag>
        );
      default:
        return <Tag>{provider}</Tag>;
    }
  };

  const getTypeTag = (type: string) => {
    return type === 'scheduled' ? (
      <Tag icon={<ClockCircleOutlined />}>Scheduled</Tag>
    ) : (
      <Tag color="orange">Manual</Tag>
    );
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp: string) => (
        <Text type="secondary">
          {new Date(timestamp).toLocaleString()}
        </Text>
      ),
      sorter: (a: ImportLog, b: ImportLog) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => getTypeTag(type),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (provider: string) => getProviderTag(provider),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
      filters: [
        { text: 'Success', value: 'success' },
        { text: 'Error', value: 'error' },
        { text: 'Running', value: 'running' },
      ],
      onFilter: (value: any, record: ImportLog) => record.status === value,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'Details',
      key: 'details',
      width: 200,
      render: (_: any, record: ImportLog) => {
        if (!record.details) return null;
        return (
          <Space direction="vertical" size="small">
            {record.details.imported !== undefined && (
              <Text type="success">Imported: {record.details.imported}</Text>
            )}
            {record.details.failed !== undefined && record.details.failed > 0 && (
              <Text type="danger">Failed: {record.details.failed}</Text>
            )}
            {record.details.skipped !== undefined && (
              <Text type="secondary">Skipped: {record.details.skipped}</Text>
            )}
            {record.details.error && (
              <Text type="danger" style={{ fontSize: '12px' }}>
                {record.details.error}
              </Text>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <FileTextOutlined /> Import Logs
        </Title>
        <Text type="secondary">
          View history of all game import operations, including scheduled and manual imports.
        </Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Logs"
              value={stats.total}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Successful"
              value={stats.success}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Errors"
              value={stats.error}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Running"
              value={stats.running}
              valueStyle={{ color: '#1890ff' }}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Import History"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={loadLogs}
            loading={loading}
          >
            Refresh
          </Button>
        }
      >
        {logs.length === 0 ? (
          <Alert
            message="No Logs Available"
            description="Import logs will appear here after you trigger an import."
            type="info"
            showIcon
          />
        ) : (
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} logs`,
            }}
            scroll={{ x: 1000 }}
          />
        )}
      </Card>
    </div>
  );
};

export default ImportLogs;

